<script setup lang="ts">
import type { InputInstance } from "element-plus";
import { computed, ref, watch } from "vue";
import type { NoteConfig, Category, Layer } from "@/types/legend.types";
import { defaultNote } from "./edit-legend.models";
import DRBtn from "@/components/UI/DRBtn.vue";
import NoteConfigItem from "./NoteConfigItem.vue";
import DragReorderList from "./DragReorderList.vue";
import { v4 } from "uuid";
import DRBtnIcon from "@/components/UI/DRBtnIcon.vue";
import ConfirmationModal from "@/components/generic/ConfirmationModal.vue";
import DREditTextDialog from "@/components/UI/feedbacks/DREditTextDialog.vue";
import { useTranslation } from "i18next-vue";

const { t } = useTranslation();

const props = defineProps<{
  notes: NoteConfig[];
  category: Category;
  layers: Layer[];
  categories: Category[];
}>();

const emits = defineEmits<{
  (e: "update-note", value: NoteConfig): void;
  (e: "update-note-categories", noteId: string, categoryIds: string[]): void;
  (e: "remove", id: string): void;
  (e: "new-layer", value: { id: string; name: string }): void;
  (e: "rename", value: string): void;
  (e: "delete-category"): void;
  (e: "reorder-notes", notes: NoteConfig[]): void;
  (
    e: "new-note",
    value: {
      note: NoteConfig;
      index?: number;
      categoryIds?: string[];
    }
  ): void;
}>();

const newNoteRef = ref<InputInstance | null>(null);
const newNoteName = ref("");

const onAddNote = () => {
  const name = (
    newNoteName.value?.length ? newNoteName.value : t(defaultNote.name)
  ).trim();
  emits("new-note", {
    note: {
      ...defaultNote,
      name,
      label: name.slice(0, 3),
      id: v4(),
    },
    categoryIds: [props.category.id],
  });
  newNoteName.value = "";
  newNoteRef.value?.focus();
};

const onDuplicateNote = (note: NoteConfig, index: number) => {
  const originalCategoryIds = props.categories
    .filter((c) => c.noteIds.includes(note.id))
    .map((c) => c.id);
  emits("new-note", {
    note: {
      ...note,
      id: v4(),
      name: note.name + " copie",
    },
    index,
    categoryIds: originalCategoryIds,
  });
};

const forbiddenNames = computed(() => {
  return props.categories
    .filter((category) => category.id !== props.category.id)
    .map((category) => category.name);
});

const showModal = ref<boolean>(false);

const rules = [
  {
    validator: (value: string) => !!value.trim()?.length,
    message: "Le nom ne peut pas être vide",
  },
  {
    validator: (value: string) => !forbiddenNames.value.includes(value),
    message: `Ce catalogue existe déjà`,
  },
];

const onOpenModal = () => {
  showModal.value = true;
};

const confirmUpdate = (newName: string) => {
  if (!newName?.length) return;
  emits("rename", newName);
};

const showDelete = ref(false);

const confirmDeletion = () => {
  showDelete.value = false;
  emits("delete-category");
};

const cancelDeletion = () => {
  showDelete.value = false;
};

const handleDelete = () => {
  showDelete.value = true;
};

// Replié par défaut : un catalogue avec beaucoup de notes prend toute la hauteur de
// l'écran, ce qui empêchait de voir (et donc de glisser-déposer) les autres catalogues
// pour les réordonner. Replié, seul l'en-tête (avec sa poignée de drag) reste visible.
const collapsed = ref(true);
// Une fois ouvert, on garde le contenu monté (v-show) au lieu de le démonter/remonter
// à chaque bascule (v-if) : évite de ré-enregistrer toutes les lignes et leurs
// DropZone/DragHandle auprès de @vue-dnd-kit/core à chaque ouverture, source de lag.
const everOpened = ref(false);
const toggleCollapsed = () => {
  collapsed.value = !collapsed.value;
};

// À la première ouverture d'un catalogue avec beaucoup de notes, monter les
// NoteConfigItem (sélecteurs, popover couleur...) d'un coup provoque un lag
// perceptible. On n'en monte qu'un premier lot tout de suite, le reste est
// révélé par petits paquets pendant les temps morts du navigateur.
const REVEAL_BATCH_SIZE = 20;
const revealedCount = ref(0);

const scheduleIdle = (callback: () => void) => {
  if (typeof window.requestIdleCallback === "function") {
    window.requestIdleCallback(callback);
  } else {
    setTimeout(callback, 0);
  }
};

const revealMore = () => {
  if (collapsed.value || revealedCount.value >= props.notes.length) return;
  revealedCount.value = Math.min(
    revealedCount.value + REVEAL_BATCH_SIZE,
    props.notes.length
  );
  if (revealedCount.value < props.notes.length) scheduleIdle(revealMore);
};

watch(collapsed, (isCollapsed) => {
  if (isCollapsed) return;
  everOpened.value = true;
  if (revealedCount.value >= props.notes.length) return;
  if (revealedCount.value === 0) {
    revealedCount.value = Math.min(REVEAL_BATCH_SIZE, props.notes.length);
  }
  scheduleIdle(revealMore);
});

// Une note ajoutée/dupliquée doit apparaître immédiatement, même si le lot
// initial n'est pas encore complet.
watch(
  () => props.notes.length,
  (newLength, oldLength) => {
    if (newLength > oldLength && revealedCount.value >= oldLength) {
      revealedCount.value = newLength;
    }
  }
);
</script>

<template>
  <div class="rounded p-3 bg-white border-gray overflow-hidden">
    <div class="d-flex justify-content-between align-items-center gap-2 pb-1">
      <div
        class="col text-dark fw-light d-flex align-items-center gap-2 cursor-pointer"
        @click="toggleCollapsed"
      >
        <i
          class="bi-chevron-down collapse-chevron"
          :class="{ 'collapse-chevron-collapsed': collapsed }"
        ></i>
        {{ $t("dashboard:catalogue") }} - {{ category.name }}
        <span class="text-black-50">({{ notes.length }})</span>
      </div>
      <div class="col-auto">
        <el-dropdown trigger="click">
          <DRBtnIcon icon="bi-three-dots" variant="flat" :size="30">
          </DRBtnIcon>

          <template #dropdown>
            <el-dropdown-menu>
              <el-dropdown-item @click="onOpenModal">Renommer</el-dropdown-item>

              <el-dropdown-item @click="handleDelete" class="text-danger">
                Supprimer
              </el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>
      </div>
    </div>
    <div v-if="everOpened" v-show="!collapsed">
      <DragReorderList
        :items="notes"
        :group="`legend-notes-${category.id}`"
        @reorder="emits('reorder-notes', $event)"
      >
        <template #default="{ item: note, index }">
          <NoteConfigItem
            v-if="index < revealedCount"
            :model-value="note"
            :layers="layers"
            :categories="categories"
            @update:modelValue="emits('update-note', $event)"
            @update:categoryIds="(ids) => emits('update-note-categories', note.id, ids)"
            @remove="emits('remove', note.id)"
            @new-layer="emits('new-layer', $event)"
            @duplicate="onDuplicateNote(note, index + 1)"
          />
          <div v-else class="note-row-placeholder"></div>
        </template>
      </DragReorderList>

      <div class="d-flex gap-3 pt-3">
        <el-input
          ref="newNoteRef"
          class="col-8 col-md-6 col-lg-4"
          size="small"
          v-model="newNoteName"
          :placeholder="$t(defaultNote.name)"
          @keyup.enter="onAddNote()"
        />
        <DRBtn
          class="col-auto"
          variant="secondary"
          :label="$t('add')"
          @click="onAddNote()"
        />
      </div>
    </div>
  </div>

  <DREditTextDialog
    v-model="showModal"
    :label="$t('new.name')"
    :title="$t('dashboard:catalogue') + ` - ${category.name} `"
    :value="category.name"
    :placeholder="$t('type.name')"
    :rules="rules"
    @confirm="confirmUpdate($event)"
  />

  <ConfirmationModal
    v-model="showDelete"
    @confirm="confirmDeletion"
    @cancel="cancelDeletion"
    :message="`Êtes-vous sûr de vouloir supprimer le catalogue ${category.name} ?`"
    danger
  />
</template>

<style lang="scss" scoped>
.cursor-pointer {
  cursor: pointer;
}

.collapse-chevron {
  transition: transform 0.15s ease;
}

.collapse-chevron-collapsed {
  transform: rotate(-90deg);
}

.note-row-placeholder {
  height: 44px;
}
</style>
